import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import type { PaymentMethod, SplitPayment } from './types';

/**
 * Split-payment allocation rules.
 *
 * The API validates a split with `sum(payments[].amount) !== amount_paid` —
 * strict equality, no tolerance — and requires at least two tenders. So the
 * modal has to reach an exact allocation before it can submit, and every
 * amount stays in paise: a rupee float round-trip (e.g. 397.95 * 100 =
 * 39794.999...) is enough to fail the whole bill.
 *
 * Kept separate from the component so the arithmetic is testable without a DOM.
 */

/** Methods a split tender can use. 'split' itself is not a tender. */
export const SPLIT_TENDER_METHODS: PaymentMethod[] = [
    'cash',
    'upi',
    'card',
    'wallet',
    'store_credit',
    'credit_note',
    'account_credit',
];

/** Sum of the tenders, in paise. */
export function allocatedTotal(splits: SplitPayment[]): number {
    return splits.reduce((sum, s) => sum + s.amount, 0);
}

/**
 * What a split must add up to. Only cash is rounded (ops-simulation T01), so
 * the cash round-off baked into `totalAmount` stands only once a cash leg is
 * in the split; without one every tender is charged the exact total.
 * `extraMethod` lets the next-tender suggestion count the tender being typed.
 */
export function splitTargetAmount(
    splits: SplitPayment[],
    totalAmount: number,
    cashRoundOff: number,
    extraMethod?: PaymentMethod,
): number {
    const hasCash = extraMethod === 'cash' || splits.some((s) => s.method === 'cash');
    return hasCash ? totalAmount : totalAmount - cashRoundOff;
}

/** Paise still to allocate. Negative when over-allocated. */
export function remainingToAllocate(splits: SplitPayment[], totalAmount: number): number {
    return totalAmount - allocatedTotal(splits);
}

/**
 * Major-unit input -> minor units, without float drift.
 *
 * `parseFloat('397.95') * 100` is 39794.999…, which floors to 39794 and leaves
 * the split one paisa short of the bill. Parsing the decimal string directly
 * avoids the intermediate float entirely.
 *
 * Both the accepted decimal places and the multiplier come from the currency.
 * Hardcoding 2dp/×100 rejected a legitimate 3-decimal OMR/BHD/KWD entry outright
 * (returning NaN, which disabled the Add button) and, where it did parse,
 * recorded one tenth of the intended amount. `currency` defaults to 'INR' so
 * INR behaviour is byte-identical.
 */
export function rupeeStringToPaise(value: string, currency: CurrencyCode = 'INR'): number {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    const parts = /^(\d*)(?:\.(\d*))?$/.exec(trimmed);
    if (!parts) return NaN;
    const decimals = getCurrencyDecimals(currency);
    const [, whole, frac = ''] = parts;
    if (frac.length > decimals) return NaN;
    const minor =
        Number(whole || '0') * getSubunitMultiplier(currency) + Number(frac.padEnd(decimals, '0'));
    return Number.isFinite(minor) ? minor : NaN;
}

/**
 * Whether the tenders can be submitted: at least two, every amount positive,
 * and the total exactly equal to the bill. Mirrors the API's own check so the
 * cashier is blocked in the modal rather than by a 400 after submitting.
 */
export function isSplitValid(splits: SplitPayment[], totalAmount: number): boolean {
    if (splits.length < 2) return false;
    if (splits.some((s) => !Number.isFinite(s.amount) || s.amount <= 0)) return false;
    return allocatedTotal(splits) === totalAmount;
}

/** Why the split cannot be submitted yet — for the cashier, not a dev. */
export function splitBlockReason(splits: SplitPayment[], totalAmount: number): string | null {
    if (splits.length === 0) return 'Add at least two payment methods';
    if (splits.some((s) => !Number.isFinite(s.amount) || s.amount <= 0)) {
        return 'Every tender needs an amount greater than zero';
    }
    if (splits.length < 2) return 'A split needs at least two payment methods';
    const remaining = remainingToAllocate(splits, totalAmount);
    if (remaining > 0) return 'Allocate the remaining balance';
    if (remaining < 0) return 'Allocated more than the bill total';
    return null;
}

/**
 * Suggested amount for the next tender: whatever is left, never negative.
 * Pre-filling this is what lets a cashier settle a bill in two taps.
 */
export function suggestNextAmount(splits: SplitPayment[], totalAmount: number): number {
    return Math.max(0, remainingToAllocate(splits, totalAmount));
}
